import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { CardComponent } from '@components/card/card.component';
import { DynamicFormQuestionComponent } from '@components/dynamic-forms/dynamic-form-question/dynamic-form-question.component';
import { OrderUpsellCheckboxComponent } from '@components/order-upsell/order-upsell-checkbox.component';
import { YesNoDetailsFieldComponent } from '@components/yes-no-details-field/yes-no-details-field.component';
import { PriorityProcessingUpsell } from '@data/tests';
import { QuestionnaireAnswers } from '@enums/questionnaire-answers';
import { MedicalHistory } from '@models/medical-history';
import { MedicalHistoryQuestion } from '@models/medical-history-question';
import { OrderUpsell } from '@models/order-upsell';
import { DynamicFormsService } from '@services/dynamic-forms.service';
import { FormService } from '@services/form.service';
import { StorageService } from '@services/storage.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-order-medical-history',
  templateUrl: './order-medical-history.component.html',
  styleUrls: ['./order-medical-history.component.scss'],
  imports: [CardComponent, YesNoDetailsFieldComponent, OrderUpsellCheckboxComponent, DynamicFormQuestionComponent],
})
export class OrderMedicalHistoryComponent implements OnInit, OnDestroy {
  @Input() public medicalHistoryData: MedicalHistory;
  @Input() public customMedicalHistoryQuestions: MedicalHistoryQuestion[] = [];

  public readonly storageService = inject(StorageService);
  public medicalHistoryForm: FormGroup;

  private readonly dynamicFormsService = inject(DynamicFormsService);
  private formService = inject(FormService);
  private subscriptions: Subscription[] = [];

  /**
   * Gets whether the component should render custom treatment questions instead of the generic ones.
   */
  public get hasCustomQuestions(): boolean {
    return this.customMedicalHistoryQuestions.length > 0;
  }

  /**
   * Gets the priority processing upsell test.
   */
  public get priorityProcessingUpsell(): OrderUpsell {
    return PriorityProcessingUpsell;
  }

  /**
   * Initializes the component.
   */
  public ngOnInit(): void {
    this.initializeForm();
  }

  /**
   * Unsubscribes from all subscriptions.
   */
  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Initializes the medical history form by setting the form group and adding validators.
   *
   * When the treatment defines custom medical history questions, their form controls are added dynamically to the group
   * and no generic validators are set up. Otherwise the standard yes/no question validators are applied.
   */
  private initializeForm(): void {
    this.medicalHistoryForm = this.formService.checkout.get('medicalHistory') as FormGroup;

    if (this.hasCustomQuestions) {
      this.clearLegacyValidators();
      this.addCustomQuestionControls();

      return;
    }

    this.addRequiredValidators();
    this.subscriptions.push(
      this.addConditionalValidators('onMedication', 'medicationDetails', 1000),
      this.addConditionalValidators('hasAllergies', 'allergiesDetails', 1000),
      this.addConditionalValidators('hasOtherConditions', 'otherConditionsDetails', 397),
    );

    if (this.medicalHistoryData) {
      this.prefillMedicalForm();
    }
  }

  /**
   * Clears all validators from the legacy yes/no and details controls.
   */
  private clearLegacyValidators(): void {
    [
      'onMedication',
      'hasAllergies',
      'hasOtherConditions',
      'medicationDetails',
      'allergiesDetails',
      'otherConditionsDetails',
    ].forEach((controlName) => {
      const control = this.medicalHistoryForm.get(controlName);

      if (control) {
        control.clearValidators();
        control.updateValueAndValidity();
      }
    });
  }

  /**
   * Adds a reactive form control for each custom question to the medical history form group, reusing the validators
   * already defined on each question.
   */
  private addCustomQuestionControls(): void {
    const customFormGroup = this.dynamicFormsService.toFormGroup(this.customMedicalHistoryQuestions);

    Object.entries(customFormGroup.controls).forEach(([name, control]) =>
      this.medicalHistoryForm.addControl(name, control),
    );
  }

  /**
   * Fill medical form with data from the previous consultation order
   */
  private prefillMedicalForm(): void {
    this.medicalHistoryForm.patchValue({
      onMedication: this.mapMedicalHistoryYesNo(this.medicalHistoryData.prescriptions),
      medicationDetails: this.mapMedicalHistoryDetails(this.medicalHistoryData.prescriptions),
      hasAllergies: this.mapMedicalHistoryYesNo(this.medicalHistoryData.allergies),
      allergiesDetails: this.mapMedicalHistoryDetails(this.medicalHistoryData.allergies),
      hasOtherConditions: this.mapMedicalHistoryYesNo(this.medicalHistoryData.health_info),
      otherConditionsDetails: this.mapMedicalHistoryDetails(this.medicalHistoryData.health_info),
    });
  }

  /**
   * Maps the medical history value to a yes/no answer based on whether the value is empty or not.
   *
   * @param {string|null} value the medical history value
   */
  private mapMedicalHistoryYesNo(value: string | null) {
    return value && value !== QuestionnaireAnswers.No ? QuestionnaireAnswers.Yes : QuestionnaireAnswers.No;
  }

  /**
   * Maps the medical history value to either the value itself or 'No' if the value is empty or 'No'.
   *
   * @param {string|null} value the medical history value
   */
  private mapMedicalHistoryDetails(value: string | null) {
    return value && value !== QuestionnaireAnswers.No ? value : QuestionnaireAnswers.No;
  }

  /**
   * Adds the required validators to the form controls.
   */
  private addRequiredValidators(): void {
    ['onMedication', 'hasAllergies', 'hasOtherConditions'].forEach((controlName) => {
      const control = this.medicalHistoryForm.get(controlName);
      control.setValidators([Validators.required]);
      control.updateValueAndValidity();
    });
  }

  /**
   * Adds conditional validators to the form controls, making the details form control required if the yes/no form
   * control is true (yes).
   *
   * @param {string} controlName the name of the yes/no form control
   * @param {string} detailsControlName the name of the details form control
   * @param {number} detailsMaxLength the maximum length of the details form control value
   *
   * @returns {Subscription} a subscription to the value changes of the yes/no form control
   */
  private addConditionalValidators(
    controlName: string,
    detailsControlName: string,
    detailsMaxLength: number,
  ): Subscription {
    const detailsControl = this.medicalHistoryForm.get(detailsControlName);

    return this.medicalHistoryForm.get(controlName).valueChanges.subscribe((value) => {
      detailsControl.setValue(null);
      if (value === QuestionnaireAnswers.Yes) {
        detailsControl.setValidators([Validators.required, Validators.maxLength(detailsMaxLength)]);
      } else {
        detailsControl.clearValidators();
      }

      detailsControl.updateValueAndValidity();
    });
  }
}
